import { Circle, CircleDot, X } from 'lucide-react';
import { type ReactElement, useCallback } from 'react';

import { cn } from '@crossnokaye/ux-common';

import { Button } from '@/ux-common/components/button/button';

import type { SelectFilterProps } from '../types';

/**
 * Single-select filter with radio button pattern.
 * Matches the old data-table filter UI.
 */
export const SelectFilter = ({
  value,
  onChange,
  options,
  onClear,
  isActive,
  onClose,
}: SelectFilterProps & { isActive: boolean; onClose: () => void }): ReactElement => {
  const handleSelect = useCallback(
    (option: string) => {
      onChange(option);
    },
    [onChange]
  );

  const handleClearItem = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();
      onChange('');
    },
    [onChange]
  );

  // Don't render if no options
  if (!options || options.length === 0) {
    return <div className="text-sm text-muted-foreground p-2">No filter options available</div>;
  }

  return (
    <>
      <div className="max-h-96 overflow-y-auto">
        {options
          .filter((option) => !!option)
          .sort((a, b) => a.localeCompare(b))
          .map((option, index) => (
            <div
              key={`${option}_${index}`}
              className={cn(
                'flex items-center justify-between cursor-pointer p-2 hover:bg-accent/50',
                option === value ? 'bg-accent/50' : ''
              )}
              onClick={(e) => {
                e.stopPropagation();
                handleSelect(option);
              }}
            >
              <div className="flex items-center gap-2">
                {option === value ? (
                  <CircleDot className="glyph glyph-sm text-foreground" aria-hidden="true" />
                ) : (
                  <Circle className="glyph glyph-sm text-foreground/50" aria-hidden="true" />
                )}
                <span className="truncate font-semibold normal-case text-sm">{option}</span>
              </div>
              {option === value && (
                <X
                  className="glyph glyph-sm ml-2 cursor-pointer text-foreground/50 hover:text-foreground"
                  onClick={handleClearItem}
                />
              )}
            </div>
          ))}
      </div>

      {/* Footer with Clear/Close buttons */}
      <div className="flex justify-end p-3 border-t border-border">
        {isActive && (
          <Button
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation();
              onClear();
            }}
            className="btn btn-tier3 btn-sm mr-2 font-semibold"
          >
            Clear
          </Button>
        )}
        <Button onClick={onClose} className="btn btn-tier2 btn-sm font-semibold">
          Close
        </Button>
      </div>
    </>
  );
};

SelectFilter.displayName = 'SelectFilter';
